import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { NavigationBar } from '@/components/NavigationBar';
import { useActiveProductSteps } from '@/hooks/use-product-selection-step';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { PRODUCT_STEPS, PRODUCT_STEP_ID_SUMMARY } from '@/lib/products';
import { type Step, type TanstackUrl, getCurrentStepIndex } from '@/lib/stepper.ts';

function createProfileStep(path: TanstackUrl): Step {
  return {
    id: 'create-profile',
    name: 'Create profile',
    url: path,
  };
}

export default function ProductStepper() {
  const navigate = useNavigate();
  const patientRecipient = useSessionStore((state) => state.patientRecipient);
  const categorySteps = useActiveProductSteps();

  const steps = useMemo(() => {
    const summaryStep = PRODUCT_STEPS.find((step) => step.id === PRODUCT_STEP_ID_SUMMARY);
    return summaryStep ? [...categorySteps, summaryStep] : categorySteps;
  }, [categorySteps]);

  const currStepIndex = getCurrentStepIndex(steps);
  const currStep = steps[currStepIndex];

  const preProductsStep = createProfileStep(
    patientRecipient === 'me' ? '/create-profile/for-me' : '/create-profile/for-someone-else'
  );
  const prevStep = currStepIndex > 0 ? steps[currStepIndex - 1]! : preProductsStep;

  if (!currStep) return null;

  const categoryPosition = categorySteps.findIndex((s) => s.id === currStep.id);
  const xOfY =
    categoryPosition >= 0
      ? `${categoryPosition + 1} of ${categorySteps.length}`
      : `${currStepIndex + 1} of ${steps.length}`;
  const description = currStep.description ? ` ${currStep.description}` : '';
  const indicator = `${xOfY}${description}`;

  return (
    <NavigationBar
      backLabel={prevStep.name}
      onBack={() => navigate({ to: prevStep.url })}
      stepIndicator={currStep.hideStepCount ? undefined : indicator}
    />
  );
}
